import { cva, VariantProps } from 'class-variance-authority';
import classNamesStyle from 'classnames';
import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../utils/cn';

const inputVariants = cva('focus:outline-none', {
  variants: {
    size: {
      xs: 'h-[26px] py-1 px-1.5 text-sm',
      sm: 'h-[32px] px-2.5 py-2 text-sm',
      md: 'h-[39px] py-2.5 px-4 text-sm',
      lg: 'h-[48px] py-3.5 px-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> &
  VariantProps<typeof inputVariants> & {
    error?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      size,
      error,
      className,
      endIcon,
      startIcon,
      startAdornment,
      endAdornment,
      value,
      ...props
    },
    ref,
  ) => (
    <div
      className={classNamesStyle(
        'relative flex min-w-0 items-center rounded-xl border bg-card text-foreground focus-within:!ring-[3px]',
        {
          'border-destructive text-destructive focus-within:!ring-destructive/20': error,
          'border-input focus-within:border-foreground focus-within:ring-ring/25': !error,
        },
        className,
      )}
    >
      {startAdornment && (
        <div
          className={classNamesStyle(
            cn(inputVariants({ size })),
            'flex !h-auto items-center justify-center border-r border-border bg-accent text-muted-foreground rounded-l-xl',
          )}
        >
          {startAdornment}
        </div>
      )}

      {startIcon && (
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">{startIcon}</div>
      )}
      <input
        type={type}
        data-1p-ignore="true"
        className={classNamesStyle(
          cn(inputVariants({ size })),
          {
            '!pl-9': !!startIcon,
            '!pr-12': !!endIcon,
            'rounded-l-none border-x-0': startAdornment,
            'rounded-r-none border-x-0': endAdornment,
          },
          'disable-autofill w-full min-w-0 rounded-[inherit] border-none bg-transparent text-foreground placeholder:text-foreground-subtle focus:outline-none disabled:bg-muted disabled:text-foreground-subtle dark:disabled:!text-neutral-500',
        )}
        ref={ref}
        onKeyDown={
          type === 'number'
            ? (e) => {
                if (e.key === '.' || e.key === 'e') {
                  e.preventDefault();
                }
              }
            : undefined
        }
        {...(value != null ? { value } : {})}
        {...props}
      />
      {endIcon && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">{endIcon}</div>
      )}
      {endAdornment && (
        <div
          className={classNamesStyle(
            cn(inputVariants({ size })),
            'flex h-[21px] items-center justify-center border-l border-border bg-accent text-muted-foreground',
          )}
        >
          {endAdornment}
        </div>
      )}
    </div>
  ),
);
